<template>
  <div class="new-base-field">
    <!-- Field label, only rendered when a `label` prop is supplied -->
    <label v-if="label" class="new-base-field__label">
      {{ label }}
      <!-- Red asterisk shown when `isRequired` is true, marking the field mandatory -->
      <span v-if="isRequired" class="new-base-field_required">*</span>
    </label>
    <!--
      Third-party phone input widget:
      - v-model binds the raw phone text to `phoneNumber`
      - :defaultCountry / :mode configure the initial country and international/national format
      - :inputOptions forwards placeholder/type and disables the library's own formatting/validation
      - @input runs `newValidateInput` to strip invalid characters (or restore the pre-dropdown value)
      - @country-changed runs `newGetCodeCountry` to track the dial code and refresh the code badge
    -->
    <vue-tel-input v-model="phoneNumber" :defaultCountry="country" :mode="mode" :autoFormat="false" :inputOptions="{
      placeholder: placeholder,
      type: type,
      showDialCode: false,
      validCharactersOnly: true,
    }" @input="newValidateInput" @country-changed="newGetCodeCountry"></vue-tel-input>
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from "vue";

import { VueTelInput } from "vue-tel-input";
import "vue-tel-input/vue-tel-input.css";

/**
 * @prop {String} value - Initial/external phone value (not directly bound; the field manages its own `phoneNumber` ref).
 * @prop {String} mode - vue-tel-input formatting mode (e.g. "international").
 * @prop {String} country - Default ISO country code used to preselect the dial code.
 * @prop {String} countryCode - External country code value (not directly consumed in this component's logic).
 * @prop {String} text - Semantic text/purpose hint, passed through as unused metadata.
 * @prop {String} label - Optional label rendered above the input.
 * @prop {Boolean} showSearchBox - Whether the country dropdown should show a search box.
 * @prop {Boolean} showDialCode - Whether to display the dial code alongside the input.
 * @prop {String} placeholder - Placeholder text forwarded to the underlying input.
 * @prop {String} type - HTML input type forwarded to the underlying input (e.g. "tel").
 * @prop {String} name - Field name, useful for forms/labels.
 * @prop {Array} error - Validation error messages for this field (optional).
 * @prop {Boolean} isPassword - Whether to render as a password-style field.
 * @prop {Boolean} isReadonly - Disables editing when true.
 * @prop {Boolean} isTextCenter - Centers the input text when true.
 * @prop {Boolean} isGray - Applies a gray background variant.
 * @prop {Boolean} isRequired - Shows the required asterisk next to the label.
 */
const props = defineProps({
  value: {
    type: String,
    default: "",
  },
  mode: {
    type: String,
    default: "international",
  },
  country: {
    type: String,
    default: "US",
  },
  countryCode: {
    type: String,
    default: "",
  },
  text: {
    type: String,
    default: "tel",
  },
  label: {
    type: String,
    default: "",
  },
  showSearchBox: {
    type: Boolean,
    default: false,
  },
  showDialCode: {
    type: Boolean,
    default: true,
  },
  placeholder: {
    type: String,
    default: "",
  },
  type: {
    type: String,
    default: "tel",
  },
  name: {
    type: String,
    default: "",
  },
  error: {
    type: Array,
    required: false,
  },
  isPassword: {
    type: Boolean,
    default: false,
  },
  isReadonly: {
    type: Boolean,
    default: false,
  },
  isTextCenter: {
    type: Boolean,
    default: false,
  },
  isGray: {
    type: Boolean,
    default: false,
  },
  isRequired: {
    type: Boolean,
    default: false,
  },
});
/**
 * @emits country-changed - Fired with the newly selected country's dial code whenever the user changes the country.
 */
const emit = defineEmits(["country-changed"]);
/** The phone number text currently typed/bound in the input (v-model target). */
const phoneNumber = ref("");
/** The currently selected country's dial code (e.g. "1", "44"), without the leading "+". */
const dialCode = ref("");

/** Snapshot of `phoneNumber` taken right before the country dropdown opens, used to restore it if needed. */
const tempNumber = ref("");
/** Whether the country selector dropdown is currently open. */
const isDropdownOpen = ref(false);

/**
 * Input handler for the phone field: while the country dropdown is closed,
 * strips any character that isn't a digit, +, (), - or whitespace; while the
 * dropdown is open, restores `phoneNumber` to the pre-open snapshot so typing
 * doesn't leak into the country search.
 * @param {string} val - Raw input value (unused; logic reads from `phoneNumber.value` directly).
 */
const newValidateInput = (val) => {
  if (!isDropdownOpen.value) {
    phoneNumber.value = phoneNumber.value.replace(/[^0-9+()-\s]/g, "");
  } else {
    phoneNumber.value = tempNumber.value;
  }
};

/**
 * Handles the vue-tel-input `country-changed` event: records the new dial
 * code, manually replaces the rendered ".vti__code" badge in the DOM with
 * one prefixed by "+", and re-emits `country-changed` to the parent.
 * @param {{dialCode: string}} countryObj - Country object supplied by vue-tel-input.
 */
const newGetCodeCountry = (countryObj) => {
  dialCode.value = `${countryObj.dialCode}`;
  const vueTelInput = document.querySelector(".vue-tel-input");
  const dropdown = vueTelInput?.querySelector(".vti__dropdown");
  vueTelInput?.querySelector(".vti__code").remove();
  dropdown.insertAdjacentHTML(
    "afterend",
    `<div class="vti__code">+${dialCode.value}</div>`
  );
  emit("country-changed", dialCode.value);
};

/**
 * Tracks the country dropdown's open/closed state by inspecting the DOM
 * directly (the library doesn't expose this via props/events): when it opens,
 * captures the current `phoneNumber` as a restore point in `tempNumber`;
 * when it closes, clears `isDropdownOpen`.
 */
watch(phoneNumber, (newVal, oldVal) => {
  const dropdown = document.querySelector(".vti__dropdown");
  if (dropdown?.classList.contains("open")) {
    if (!isDropdownOpen.value) {
      isDropdownOpen.value = true;
      tempNumber.value = oldVal;
    }
  } else {
    isDropdownOpen.value = false;
  }
});

/** On mount, injects the initial (unprefixed) dial-code badge next to the country dropdown. */
onMounted(() => {
  newInsertCountryCode();
});

/**
 * Inserts the dial-code badge (".vti__code") into the DOM right after the
 * country dropdown trigger, using the current `dialCode` value.
 */
const newInsertCountryCode = () => {
  const vueTelInput = document.querySelector(".vue-tel-input");
  const dropdown = vueTelInput?.querySelector(".vti__dropdown");
  dropdown.insertAdjacentHTML(
    "afterend",
    `<div class="vti__code">${dialCode.value}</div>`
  );
};
</script>
<style lang="scss">
.new-base-field {
  position: relative;
  font-size: 20px;
  // margin-bottom: 10px;

  &__label {
    // color: #0D141C;
    color: var(--color-accent-primary) !important;
    min-height: 30px;
    margin-bottom: 0;
    font-size: 14px !important;
    font-weight: 700 !important;
  }

  &_required {
    /* OLD: color: #ff0000; */
    color: var(--color-form-input-border-error);
  }

  &__control {
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
    height: 48px;
    padding: 0 15px;
    line-height: 145%;
    // border: 1px solid rgba(23, 23, 25, 0.1);
    border-radius: 5.285px;
    border: 1px solid var(--color-new-input-field-border) !important;
    background: var(--color-input-field-bg) !important;
    border-radius: 12px;
    transition: 0.2s ease-in-out;

    &:focus-within {
      /* OLD: border-color: #a1a1aa; */
      border-color: var(--color-grey-quinary);
    }
  }

  &__input {
    position: relative;
    width: 100%;
    border: none;
    background: transparent;
    /* OLD: color: #9696a2; */
    color: var(--color-grey-quinary);
    font-size: 14px;

    &::placeholder {
      /* OLD: color: #52525b; */
      color: var(--color-grey-octonary);
      font-size: 16px;
    }
  }

  &__slot {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 4px 0;
    height: 100%;

    span {
      width: 25px;

      &:before {
        font-size: 26px;
      }
    }
  }

  &__error {
    margin-top: 2px;
    border-radius: 10px;
    font-size: 12px;
    /* OLD: color: #f87171; */
    color: var(--color-error-text);
    padding: 2px 10px;
  }

  .ui-kit {
    &__icon {
      &::before {
        font-size: 20px;
      }
    }
  }

  &_is-password {
    &:focus-within {
      .ui-kit {
        &__icon {
          &::before {
            /* OLD: color: #a3e635; */
            color: var(--color-brand-lime);
          }
        }
      }
    }
  }

  &_is-readonly {
    .new-base-field__control {
      background: rgba(255, 255, 255, 0.05);
      cursor: no-drop;
    }

    .new-base-field__input {
      &:read-only {
        /* OLD: color: #3f3f46; */
        color: var(--color-grey-undenary);
        cursor: no-drop;
      }
    }
  }

  &_is-border {
    .new-base-field__control {
      border: 1px solid rgba(255, 255, 255, 0.2);

      &:focus-within {
        /* OLD: border-color: #a1a1aa; */
        border-color: var(--color-grey-quinary);
      }
    }
  }

  &_is-bottom-border-focus {
    gap: 8px;
    display: flex;
    flex-direction: column;

    .new-base-field__label {
      font-size: 12px;
      line-height: 16px;
      min-height: auto;

    }

    .new-base-field__control {
      background: var(--color-background-secondary);
      border: none;
      border-bottom: 1px solid transparent;
      transition: border-bottom 0.3s ease;
    }

    .new-base-field__control:focus-within {
      border: none;
      border-bottom: 1px solid var(--color-foreground-primary);
    }

    .new-base-field__input {
      font-size: 14px;
      line-height: 20px;
      color: var(--color-foreground-primary);
    }

    .new-base-field__input::placeholder {
      font-size: 14px;
      line-height: 20px;
      /* OLD: color: #52525b; */
      color: var(--color-grey-octonary);
    }

    .new-base-field__error {
      /* OLD: color: #f87171; */
      color: var(--color-error-text);
      padding: 0;
    }

    .new-base-field__control:has(+ .new-base-field__error) {
      border: none;
      /* OLD: border-bottom: 1px solid #dc2626; */
      border-bottom: 1px solid var(--color-error-text);
    }
  }

  &_is-search {
    .new-base-field__control {
      background: var(--color-search-bg);
    }
  }

  &_is-gray {
    .new-base-field__control {
      background: var(--color-background-secondary);
    }
  }

  &_is-center &__input {
    text-align: center;
  }

  &_is-center {
    min-width: 100px;
  }

  @include _380 {
    font-size: 14px;

    &__control {
      font-size: 14px;
      height: 44px;
    }
  }
}
.new-base-field_required {
  color: var(--color-form-input-border-error);
}
.login {
  &__field {
    &-dropdown {
      .vue-tel-input {
        position: relative;
        z-index: 100;
        height: 48px;
        border-radius: 10px;
        // border: 1px solid #a1a1aa;
        /* OLD: border: 1px solid #CFDBE8; */
        border: 1px solid var(--color-form-input-border);
        background-color: var(--color-theme-wise-background);


        .vti__input {
          background: var(--color-theme-wise-background);
          border-radius: 0 10px 10px 0;
          color: var(--color-foreground-primary);
          font-size: 16px;
          padding-left: 10px;
          padding-right: 10px;
          // font-family: "SF Pro Text", sans-serif;
          font-family: $poppins;
          color: var(--color-text-primary);

          &::placeholder {
            color: var(--color-foreground-secondary);
            font-size: 14px;
            transition: opacity 0.3s ease-in-out;
          }

          &:focus::placeholder {
            opacity: 0;
          }

          &.vti__search_box {
            width: calc(100% - 5px);
            border-radius: 5px;
          }
        }

        .vti__phone {
          padding-left: 0;
        }

        .vti__dropdown {
          position: static;
          /* OLD: border-right: 1px solid #a1a1aa; */
          border-right: 1px solid var(--color-grey-quinary);

          &:hover,
          &.open {
            background-color: transparent;
          }

          &:focus-visible {
            outline: none;
          }

          &-arrow {
            /* OLD: color: #a1a1aa; */
            color: var(--color-grey-quinary);
          }

          &-list {
            width: 100%;
            border-radius: 10px;
            // background-color: var(--color-background-primary);
            /* OLD: border: 1px solid #a1a1aa; */
            border: 1px solid var(--color-grey-quinary);
            max-height: 136px;
            left: 0;

            &.below {
              top: calc(100% + 10px);
              scrollbar-width: none;
            }
          }

          &-item {
            padding: 10px;
            font-size: 13px;
            background-color: var(--color-theme-wise-background);

            strong {
              font-weight: normal;
            }

            &.highlighted {
              background-color: var(--color-theme-wise-hover);
            }
          }
        }

        .vti__code {
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          padding: 0 8px 0 10px;
          opacity: 0.5;
          font-family: "SF Pro Text", sans-serif;
          // color: #4D7399;
        }

        &:focus-within {
          box-shadow: none;
        }
      }

      &.form-field--dropdown {
        .vue-tel-input {
          background: rgba(255, 255, 255, 0.15);
          backdrop-filter: blur(60px);
          border: none;

          .vti {
            &__input {
              background: transparent;
            }

            &__phone {
              &::placeholder {
                color: rgba(255, 255, 255, 0.5);
              }
            }

            &__dropdown {
              border-right: 1px solid rgba(142, 108, 134, 0.6);

              &-arrow {
                /* OLD: color: #fff; */
                color: var(--color-white);
              }

              &-list {
                /* OLD: background: #8d386c; */
                background: var(--color-country-selector-bg);
                backdrop-filter: blur(60px);
                scrollbar-width: thin;
                scrollbar-color: #930036 #d070b4;
              }
            }
          }
        }
      }
    }
  }
}
</style>
